import type { AdminOverview, AdminProject } from "@gjwtrack/domain";
import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { Centered } from "../../App.tsx";
import { useClient } from "../../lib/client.ts";
import { Button, Input, InputSize } from "../../ui/ring.ts";
import { SimpleSelect } from "../../ui/SimpleSelect.tsx";
import { ProjectFields } from "./ProjectFields.tsx";
import { ProjectPeople } from "./ProjectPeople.tsx";
import { dateText, Row, SquareIcon, userLabel, type Run } from "./shared.tsx";

/** The four settings pages GTrack has. YouTrack's Version Control, Notifications, Build Servers, Workflows and Apps are not implemented and are left out rather than shown dead. */
export const PROJECT_TABS = [
  ["general", "General"],
  ["people", "People"],
  ["fields", "Custom Fields"],
  ["time", "Time Tracking"],
] as const;

export type ProjectTab = (typeof PROJECT_TABS)[number][0];

/** `/admin/projects/:id/<tab>` → the tab's label, for the breadcrumbs. */
export function projectTabLabel(slug: string): string {
  return PROJECT_TABS.find(([s]) => s === slug)?.[1] ?? "General";
}

/**
 * YouTrack's project settings: its own left column (the project, the four pages,
 * who owns it and since when) instead of the administration nav, with the page
 * itself on the right. Everything saves as you go; there is no page-level Save.
 */
export function ProjectSettings({ data, run, reload }: { data: AdminOverview; run: Run; reload: () => Promise<void> }) {
  const { id = "" } = useParams();
  const { pathname } = useLocation();
  const p = data.projects.find((x) => x.id === Number(id));
  if (!p) return <Centered>No such project.</Centered>;
  const base = `/admin/projects/${p.id}`;
  return (
    <>
      <aside className="flex w-60 shrink-0 flex-col border-r border-line">
        <div className="flex items-center gap-2.5 px-4 py-4">
          <SquareIcon text={p.name} size={32} />
          <div className="min-w-0">
            <div className="truncate font-medium" title={p.name}>
              {p.name}
            </div>
            <div className="font-mono text-xs text-muted">{p.key}</div>
          </div>
        </div>
        <nav className="py-1">
          {PROJECT_TABS.map(([slug, label]) => (
            <Link key={slug} to={`${base}/${slug}`} className={`nav-item mx-2 ${pathname === `${base}/${slug}` ? "active" : ""}`}>
              {label}
            </Link>
          ))}
        </nav>
        <div className="flex-1" />
        <div className="border-t border-line px-4 py-3 text-xs text-muted">
          <div>Owned by {p.ownerId === null ? "nobody" : userLabel(data, p.ownerId)}</div>
          <div>Created on {dateText(p.createdAt)}</div>
        </div>
      </aside>
      <div className="min-w-0 flex-1 overflow-auto px-8 py-5">
        <Routes>
          <Route path="general" element={<General p={p} data={data} run={run} />} />
          <Route path="people" element={<ProjectPeople p={p} data={data} run={run} reload={reload} />} />
          <Route path="fields" element={<ProjectFields p={p} data={data} run={run} reload={reload} />} />
          <Route path="time" element={<TimeTracking p={p} run={run} />} />
          <Route path="*" element={<Navigate to={`${base}/general`} replace />} />
        </Routes>
      </div>
    </>
  );
}

export function SettingsTitle({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
      <div className="flex-1" />
      {action}
    </div>
  );
}

function General({ p, data, run }: { p: AdminProject; data: AdminOverview; run: Run }) {
  const me = useClient((s) => s.me);
  const [name, setName] = useState(p.name);
  const [key, setKey] = useState(p.key);
  useEffect(() => {
    setName(p.name);
    setKey(p.key);
  }, [p.name, p.key]);
  return (
    <div className="max-w-3xl">
      <SettingsTitle title="General" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run("updateProject", { projectId: p.id, name: name.trim(), key: key.trim() });
        }}
      >
        <Row label="Name">
          <Input size={InputSize.FULL} value={name} onChange={(e) => setName(e.target.value)} />
        </Row>
        <Row label="Project ID" hint="Issue keys are built from it: renaming it renames every issue of the project.">
          <Input inputClassName="w-40 font-mono" value={key} onChange={(e) => setKey(e.target.value)} />
        </Row>
        <div className="mt-3">
          <Button primary type="submit" disabled={name.trim() === p.name && key.trim() === p.key}>
            Save
          </Button>
        </div>
      </form>

      <div className="mt-6">
        <Row label="Owner" hint="YouTrack's project leader. Shown on the People page; it grants no permissions by itself.">
          <SimpleSelect
            value={p.ownerId === null ? "" : String(p.ownerId)}
            placeholder="Nobody"
            options={[{ value: "", label: "Nobody" }, ...data.users.filter((u) => u.status !== "disabled").map((u) => ({ value: String(u.id), label: `${u.name} (${u.email})` }))]}
            onChange={(v) => void run("updateProject", { projectId: p.id, ownerId: v ? Number(v) : null })}
          />
        </Row>
        {me?.isAdmin && (
          <Row label="Archive" hint="An archived project is hidden from the issue list, the boards and the query language; its issues stay.">
            <Button onClick={() => void run("archiveProject", { projectId: p.id, archived: !p.archived })}>{p.archived ? "Restore project" : "Archive project"}</Button>
          </Row>
        )}
      </div>
    </div>
  );
}

function TimeTracking({ p, run }: { p: AdminProject; run: Run }) {
  const schema = useClient((s) => s.schema);
  if (!schema) return null;
  const periodFields = (schema.projectFieldsByProject.get(p.id) ?? [])
    .map((pf) => schema.fieldsById.get(pf.fieldId))
    .filter((d): d is NonNullable<typeof d> => !!d && d.kind === "period");
  return (
    <div className="max-w-3xl">
      <SettingsTitle title="Time Tracking" />
      <Row label="Spent time field" hint="Work items logged on an issue are summed into this period field. Off means work items are still recorded but no field carries the total.">
        <SimpleSelect
          value={p.spentTimeFieldId === null ? "" : String(p.spentTimeFieldId)}
          placeholder="Off"
          options={[{ value: "", label: "Off" }, ...periodFields.map((d) => ({ value: String(d.id), label: d.name }))]}
          onChange={(v) => void run("updateProject", { projectId: p.id, spentTimeFieldId: v ? Number(v) : null })}
        />
      </Row>
      {periodFields.length === 0 && (
        <p className="mt-3 text-xs text-faint">
          This project has no period field yet. Add one on <Link to={`/admin/projects/${p.id}/fields`} className="text-value hover:underline">Custom Fields</Link>.
        </p>
      )}
    </div>
  );
}
